<template>
    <div class="flex flex-col items-center gap-4 text-center py-2">
        <span class="launched-badge" aria-hidden="true">
            <i class="bi bi-check-lg" aria-hidden="true"></i>
        </span>
        <h2 class="display-serif text-ink">{{ t("reflections.sixty.launched.title") }}</h2>
        <p v-if="selfAssessEnabled" class="body-small text-ink-muted max-w-[460px]">
            {{ t("reflections.sixty.launched.selfAssessBody") }}
        </p>
        <p v-else class="body-small text-ink-muted max-w-[460px]">{{ t("reflections.sixty.launched.invitedBody") }}</p>
        <p v-if="closesDate" class="scaffold-details-small text-ink-muted">{{ t("reflections.sixty.launched.closesOn", { date: closesDate }) }}</p>

        <BaseCard v-if="selfAssessEnabled" padding="md" class="w-full max-w-[460px] mt-3 flex items-center justify-between gap-4 text-left">
            <div class="flex flex-col gap-1 min-w-0">
                <span class="scaffold-body-small text-ink-muted">{{ t("reflections.sixty.launched.nextStep") }}</span>
                <span class="body-semibold text-ink">{{ t("reflections.sixty.launched.completeSelf") }}</span>
                <span v-if="closes" class="scaffold-details-small text-ink-muted">{{ closes }}</span>
            </div>
            <BaseButton theme="primary" size="sm" @click="emit('start')">{{ t("reflections.sixty.launched.start") }}</BaseButton>
        </BaseCard>
        <BaseButton v-else theme="primary" size="sm" class="mt-3" @click="emit('done')">{{ t("common.done") }}</BaseButton>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import { closesLabel, formatDate } from "~vue/utils/threeSixty";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    selfAssessEnabled: boolean;
    closesAt?: string | null;
}

const { selfAssessEnabled, closesAt = null } = defineProps<Props>();

const { t } = useI18n();

const closes = computed(() => closesLabel(closesAt));
const closesDate = computed(() => formatDate(closesAt, "long"));
const emit = defineEmits<{ start: []; done: [] }>();
</script>

<style scoped>
.launched-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    border-radius: 9999px;
    background: var(--color-chip-status-done-bg);
    color: var(--color-chip-status-done-text);
    font-size: 30px;
    animation: launched-pop 0.45s ease-out both;
}

@keyframes launched-pop {
    0% {
        transform: scale(0.6);
        opacity: 0;
    }

    60% {
        transform: scale(1.08);
    }

    100% {
        transform: scale(1);
        opacity: 1;
    }
}

@media (prefers-reduced-motion: reduce) {
    .launched-badge {
        animation: none;
    }
}
</style>
